import { useCallback, useEffect, useRef, useState } from 'react';

/** Status der Kamera-Berechtigung. */
export type CameraPermissionStatus = 'idle' | 'requesting' | 'granted' | 'denied' | 'error';

/** Rueckgabewert von useCamera. */
export interface UseCameraReturn {
  /** Ref fuer das Video-Element. */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Aktueller Berechtigungs-Status. */
  permissionStatus: CameraPermissionStatus;
  /** Fehlermeldung wenn vorhanden. */
  errorMessage: string | null;
  /** Kamera starten. */
  startCamera: () => Promise<void>;
  /** Kamera stoppen. */
  stopCamera: () => void;
}

/**
 * Hook fuer den Kamera-Zugriff via navigator.mediaDevices.
 * Verwaltet Berechtigungs-Status, Stream und Video-Element.
 *
 * @returns Kamera-State und Steuerungsfunktionen
 *
 * @example
 * ```tsx
 * const { videoRef, permissionStatus, startCamera } = useCamera();
 * useEffect(() => { startCamera(); }, []);
 * return <video ref={videoRef} autoPlay playsInline muted />;
 * ```
 */
export function useCamera(): UseCameraReturn {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [permissionStatus, setPermissionStatus] = useState<CameraPermissionStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermissionStatus('error');
      setErrorMessage('Kamera wird von diesem Browser nicht unterstuetzt.');
      return;
    }

    setPermissionStatus('requesting');
    setErrorMessage(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });

      streamRef.current = stream;
      setPermissionStatus('granted');
      // srcObject wird im useEffect gesetzt, nachdem React das <video>-Element gerendert hat.
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setPermissionStatus('denied');
        setErrorMessage(
          'Kamera-Berechtigung verweigert. Bitte in den Browser-Einstellungen erlauben.',
        );
      } else {
        setPermissionStatus('error');
        setErrorMessage('Kamera konnte nicht gestartet werden.');
      }
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setPermissionStatus('idle');
  }, []);

  // Bindet den Stream ans Video-Element nachdem React es gerendert hat.
  // Das <video>-Element existiert erst nach dem Re-Render durch setPermissionStatus('granted').
  useEffect(() => {
    if (permissionStatus === 'granted' && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [permissionStatus]);

  useEffect(() => {
    const video = videoRef.current;
    return () => {
      if (video?.srcObject) {
        const tracks = (video.srcObject as MediaStream).getTracks();
        tracks.forEach((track) => track.stop());
        video.srcObject = null;
      }
    };
  }, []);

  return { videoRef, permissionStatus, errorMessage, startCamera, stopCamera };
}
